import { normalizeSearchString } from "src/common/utils/common";
import {
	GroupContentItem,
	GroupDescriptor,
	GroupedListDescriptor,
	GroupListDescriptor,
	ListDescriptor,
	RecordDescriptor,
	SectionContentItem,
	SectionDescriptor,
	TableDescriptor,
	TableGroupDescriptor,
	TableGroupListDescriptor,
	TimelineDescriptor
} from "src/modules/shared/dashboard/types";

export function filterSectionList(
	list: SectionDescriptor[],
	query: string
): SectionDescriptor[] {
	const result: SectionDescriptor[] = [];
	const normalizedQuery = normalizeSearchString(query);
	for (const sectionA of list) {
		const sectionB = filterSection(sectionA, normalizedQuery);
		if (sectionB !== undefined) {
			result.push(sectionB);
		}
	}
	return result;
}

function filterSection(
	section: SectionDescriptor,
	query: string
): SectionDescriptor | undefined {
	if (stringIncludesQuery(section.label, query)) {
		return section;
	}
	const content: SectionContentItem[] = [];
	for (const itemA of section.content) {
		let itemB: SectionContentItem | undefined;
		switch (itemA.type) {
			case "group-list":
				itemB = filterGroupList(itemA, query);
				break;
			case "group":
				itemB = filterGroup(itemA, query);
				break;
			case "table":
				itemB = filterTable(itemA, query);
				break;
			case "table-group-list":
				itemB = filterTableGroupList(itemA, query);
				break;
			case "grouped-list":
				itemB = filterGroupedList(itemA, query);
				break;
			case "list":
				itemB = filterList(itemA, query);
				break;
			case "record":
				itemB = filterRecord(itemA, query);
				break;
			case "timeline":
				itemB = filterTimeline(itemA, query);
				break;
		}
		if (itemB !== undefined) {
			content.push(itemB);
		}
	}
	if (content.length !== 0) {
		return { ...section, content };
	}
	return undefined;
}

function filterGroupList(
	list: GroupListDescriptor,
	query: string
): GroupListDescriptor | undefined {
	const groups: GroupDescriptor[] = [];
	for (const groupA of list.groups) {
		const groupB = filterGroup(groupA, query);
		if (groupB !== undefined) {
			groups.push(groupB);
		}
	}
	if (groups.length !== 0) {
		return { ...list, groups };
	}
	return undefined;
}

function filterGroup(
	group: GroupDescriptor,
	query: string
): GroupDescriptor | undefined {
	if (optionalStringIncludesQuery(group.label, query)) {
		return group;
	}
	const content: GroupContentItem[] = [];
	for (const itemA of group.content) {
		let itemB: GroupContentItem | undefined;
		switch (itemA.type) {
			case "table":
				itemB = filterTable(itemA, query);
				break;
			case "table-group-list":
				itemB = filterTableGroupList(itemA, query);
				break;
			case "grouped-list":
				itemB = filterGroupedList(itemA, query);
				break;
			case "list":
				itemB = filterList(itemA, query);
				break;
			case "record":
				itemB = filterRecord(itemA, query);
				break;
		}
		if (itemB !== undefined) {
			content.push(itemB);
		}
	}
	if (content.length !== 0) {
		return { ...group, content };
	}
	return undefined;
}

function filterTable(
	table: TableDescriptor,
	query: string
): TableDescriptor | undefined {
	if (optionalStringIncludesQuery(table.label, query)) {
		return table;
	}
	if (table.columns.some(col => stringIncludesQuery(col, query))) {
		return table;
	}
	if (
		table.rows.some(row => row.some(col => stringIncludesQuery(col, query)))
	) {
		return table;
	}
	return undefined;
}

function filterTableGroupList(
	list: TableGroupListDescriptor,
	query: string
): TableGroupListDescriptor | undefined {
	if (optionalStringIncludesQuery(list.label, query)) {
		return list;
	}
	if (list.columns.some(col => stringIncludesQuery(col, query))) {
		return list;
	}
	const groups: TableGroupDescriptor[] = [];
	for (const groupA of list.groups) {
		const groupB = filterTableGroup(groupA, query);
		if (groupB !== undefined) {
			groups.push(groupB);
		}
	}
	if (groups.length !== 0) {
		return { ...list, groups };
	}
	return undefined;
}

function filterTableGroup(
	group: TableGroupDescriptor,
	query: string
): TableGroupDescriptor | undefined {
	if (
		optionalStringIncludesQuery(group.label, query) ||
		group.rows.some(row => row.some(col => stringIncludesQuery(col, query)))
	) {
		return group;
	}
	return undefined;
}

function filterGroupedList(
	group: GroupedListDescriptor,
	query: string
): GroupedListDescriptor | undefined {
	const hasGroups = group.groups.some(group => {
		if (optionalStringIncludesQuery(group.label, query)) {
			return true;
		}
		return group.items.some(item => itemIncludesQuery(item, query));
	});
	if (hasGroups) {
		return group;
	}
	return undefined;
}

function filterList(
	list: ListDescriptor,
	query: string
): ListDescriptor | undefined {
	if (optionalStringIncludesQuery(list.label, query)) {
		return list;
	}
	const hasItems = list.items.some(item => itemIncludesQuery(item, query));
	if (hasItems) {
		return list;
	}
	return undefined;
}

function filterRecord(
	record: RecordDescriptor,
	query: string
): RecordDescriptor | undefined {
	if (itemIncludesQuery(record, query)) {
		return record;
	}
	return undefined;
}

function filterTimeline(
	timeline: TimelineDescriptor,
	query: string
): TimelineDescriptor | undefined {
	const items = timeline.items.filter(
		item =>
			stringIncludesQuery(item.label, query) ||
			(Array.isArray(item.value.data)
				? item.value.data.some(value =>
						stringIncludesQuery(value, query)
					)
				: stringIncludesQuery(item.value.data, query))
	);
	if (items.length !== 0) {
		return { ...timeline, items };
	}
	return undefined;
}

function itemIncludesQuery(
	item: { label?: string; value?: string },
	query: string
): boolean {
	return (
		optionalStringIncludesQuery(item.label, query) ||
		optionalStringIncludesQuery(item.value, query)
	);
}

function stringIncludesQuery(str: string, query: string): boolean {
	return normalizeSearchString(str).includes(query);
}

function optionalStringIncludesQuery(
	str: string | undefined,
	query: string
): boolean {
	return str !== undefined && normalizeSearchString(str).includes(query);
}
